import React from 'react'
import {
  Box,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableRow,
  TextField,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { isEmpty, map, noop } from 'lodash'
import moment from 'moment'
import PropTypes from 'prop-types'

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.root}`]: {
    padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
    borderBottom: `none`,
  },
  [`&.${tableCellClasses.body}`]: {
    width: '50%',
  },
}))

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.background.default,
  },
}))

function CarDataTable({ data, onInputChange }) {
  const handleInputChange = (e) => {
    e.preventDefault()
    onInputChange(e.target.name, e.target.value)
  }

  const renderInput = (
    isDate,
    name,
    value,
    isSelect,
    disabled,
    endAdornment,
    options = [],
  ) => {
    const input = (() => {
      switch (true) {
        case isDate:
          return (
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                inputFormat="DD.MM.YYYY"
                value={value || ''}
                onChange={(newValue) => {
                  const now = moment()
                  const hours = now.hours()
                  const formattedNewValue = moment(newValue)
                    .hours(hours)
                    .format()

                  if (formattedNewValue !== 'Invalid date') {
                    onInputChange(name, formattedNewValue)
                  } else {
                    onInputChange(name, '')
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name={name}
                    fullWidth
                    inputProps={{ ...params.inputProps, sx: { p: 1 } }}
                    error={false}
                  />
                )}
              />
            </LocalizationProvider>
          )

        case isSelect:
          return !isEmpty(options) ? (
            <Select
              name={name}
              value={value || ''}
              fullWidth
              onChange={handleInputChange}
              disabled={disabled}
              inputProps={{ sx: { p: 1 } }}
            >
              {options.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          ) : (
            <TextField
              name={name}
              value={value || ''}
              disabled
              fullWidth
              inputProps={{ sx: { p: 1 } }}
            />
          )

        default:
          return (
            <TextField
              name={name}
              type={name === 'mileage' ? 'number' : 'text'}
              value={value || ''}
              fullWidth
              onChange={handleInputChange}
              inputProps={{ sx: { p: 1 } }}
            />
          )
      }
    })()

    return endAdornment ? (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ width: '100%' }}>{input}</Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          {endAdornment}
        </Box>
      </Box>
    ) : (
      input
    )
  }

  return isEmpty(data) ? null : (
    <Table>
      <TableBody>
        {map(
          data,
          (
            {
              name,
              label,
              value,
              isDate,
              isEditable,
              isSelect,
              disabled,
              options,
              endAdornment,
            },
            key,
          ) => (
            <StyledTableRow key={key}>
              <StyledTableCell>
                {label}
                {isEditable ? (
                  renderInput(
                    isDate,
                    name,
                    value,
                    isSelect,
                    disabled,
                    endAdornment,
                    options,
                  )
                ) : (
                  <strong style={{ float: 'right' }}>{value}</strong>
                )}
              </StyledTableCell>
            </StyledTableRow>
          ),
        )}
      </TableBody>
    </Table>
  )
}

CarDataTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      label: PropTypes.string,
      value: PropTypes.any,
      isDate: PropTypes.bool,
      isEditable: PropTypes.bool,
      isSelect: PropTypes.bool,
      disabled: PropTypes.bool,
      options: PropTypes.array,
      endAdornment: PropTypes.node,
    }),
  ),
  onInputChange: PropTypes.func,
}

CarDataTable.defaultProps = {
  data: [],
  onInputChange: noop,
}

export default CarDataTable
